import { createRouter, createWebHistory } from "vue-router";
import HomeView from "@/views/HomeView.vue";
import store from "@/store";

const ONBOARDING_PATH = "/admin/onboarding";

/** Rotas admin acessíveis mesmo com onboarding pendente (evita prender o usuário). */
const ONBOARDING_ALLOWLIST = [ONBOARDING_PATH];

function readCurrentPdvId() {
  try {
    const stored = sessionStorage.getItem("currentPointOfSale");
    if (!stored) return null;
    return JSON.parse(stored)?.id ?? null;
  } catch (error) {
    console.error("Erro ao ler pdvId do sessionStorage:", error);
    return null;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "home",
      component: HomeView,
    },
    {
      path: "/forgot-password",
      name: "forgotPassword",
      component: () =>
        import(
          /* webpackChunkName: "forgotPassword" */ "@/components/login/forgotPassword.vue"
        ),
    },
    {
      path: "/verify-email",
      name: "verifyEmail",
      component: () =>
        import(
          /* webpackChunkName: "verifyEmail" */ "@/components/login/verifyEmail.vue"
        ),
    },
    {
      path: "/new-password",
      name: "newPassword",
      component: () =>
        import(
          /* webpackChunkName: "newPassword" */ "@/components/login/newPassword.vue"
        ),
    },
    {
      path: "/user-auth",
      name: "userAuthenticate",
      component: () =>
        import(
          /* webpackChunkName: "userAuth" */ "@/components/userAuth/userAuthenticate.vue"
        ),
    },
    {
      path: "/dashboard",
      name: "content",
      component: () =>
        import(
          /* webpackChunkName: "dashboard" */ "@/components/content/content.vue"
        ),
    },
    {
      path: "/admin/orders",
      name: "admin-orders",
      component: () =>
        import(
          /* webpackChunkName: "adminOrders" */ "@/views/admin/orders/OrdersPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/products",
      name: "admin-products",
      component: () =>
        import(
          /* webpackChunkName: "adminProducts" */ "@/views/admin/products/ProductsPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/products/new",
      name: "admin-product-create",
      component: () =>
        import(
          /* webpackChunkName: "adminProductForm" */ "@/views/admin/products/ProductFormPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/products/edit/:id",
      name: "admin-product-edit",
      component: () =>
        import(
          /* webpackChunkName: "adminProductForm" */ "@/views/admin/products/ProductFormPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/products/ifood-import",
      name: "admin-ifood-import",
      component: () =>
        import(
          /* webpackChunkName: "adminIFoodImport" */ "@/views/admin/products/IFoodImportPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/configuration",
      name: "admin-store-configuration",
      component: () =>
        import(
          /* webpackChunkName: "adminStoreConfiguration" */ "@/views/admin/configuration/ConfigurationPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/pdv",
      name: "admin-pdv",
      component: () =>
        import(
          /* webpackChunkName: "adminPdv" */ "@/views/admin/pdv/PDVPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/pdv/customer",
      name: "admin-pdv-customer",
      component: () =>
        import(
          /* webpackChunkName: "adminPdvCustomer" */ "@/views/admin/pdv/PdvCustomerForm.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/pdv/summary",
      name: "admin-pdv-summary",
      component: () =>
        import(
          /* webpackChunkName: "adminPdvSummary" */ "@/views/admin/pdv/PdvSummary.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas",
      name: "admin-charging-dashboard",
      component: () =>
        import(
          /* webpackChunkName: "adminChargingDashboard" */ "@/views/admin/charging/ChargingDashboardPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas/carteira",
      name: "admin-charging-wallet",
      component: () =>
        import(
          /* webpackChunkName: "adminChargingWallet" */ "@/views/admin/charging/ChargingWalletPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas/saldo",
      redirect: "/admin/cobrancas/carteira",
    },
    {
      path: "/admin/cobrancas/saque",
      redirect: "/admin/cobrancas/carteira",
    },
    {
      path: "/admin/onboarding",
      name: "admin-onboarding",
      component: () =>
        import(
          /* webpackChunkName: "adminOnboarding" */ "@/views/admin/onboarding/OnboardingPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas/historico-saques",
      name: "admin-charging-withdrawals-history",
      component: () =>
        import(
          /* webpackChunkName: "adminChargingWithdrawalsHistory" */ "@/views/admin/charging/ChargingWithdrawalsHistoryPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas/transacoes",
      name: "admin-charging-transactions",
      component: () =>
        import(
          /* webpackChunkName: "adminChargingTransactions" */ "@/views/admin/charging/ChargingTransactionsPage.vue"
        ),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/cobrancas/chave-pix",
      redirect: "/admin/cobrancas/carteira",
    },
    {
      path: "/cart",
      name: "cart",
      component: () =>
        import(/* webpackChunkName: "cart" */ "@/catalog/cart/cartSingle.vue"),
    },
    {
      path: "/order/myorder",
      name: "myOrder",
      component: () =>
        import(
          /* webpackChunkName: "myOrder" */ "@/catalog/orders/myOrders.vue"
        ),
    },
    {
      path: "/order/:myorderId",
      name: "myOrdersDetails",
      component: () =>
        import(
          /* webpackChunkName: "myOrderDetails" */ "@/catalog/orders/myOrdersDetails.vue"
        ),
    },

    // Rotas antigas mantidas para compatibilidade (redirect para as novas)
    {
      path: "/dashboard-products",
      redirect: "/admin/products",
    },
    {
      path: "/product/new",
      redirect: "/admin/products/new",
    },
    {
      path: "/product/edit/",
      redirect: "/admin/products",
    },
    {
      path: "/product/configuration-edit/",
      redirect: "/admin/products",
    },
    {
      path: "/product/configuration",
      redirect: "/admin/products",
    },
    {
      path: "/admin/products/configuration",
      redirect: "/admin/products",
    },
    {
      path: "/admin/products/configuration-edit",
      redirect: "/admin/products",
    },
    {
      path: "/product/:id",
      name: "product",
      component: () =>
        import(
          /* webpackChunkName: "product" */ "@/modules/product/product.vue"
        ),
    },
    {
      path: "/summary",
      name: "summary",
      component: () =>
        import(
          /* webpackChunkName: "summary" */ "@/catalog/summary/summaryCart.vue"
        ),
    },
    {
      path: "/personal-data",
      name: "personalData",
      component: () =>
        import(
          /* webpackChunkName: "personalData" */ "@/catalog/personalData/personalData.vue"
        ),
    },
    {
      path: "/verify-customer",
      name: "verifyCustomer",
      component: () =>
        import(
          /* webpackChunkName: "verifyCustomer" */ "@/catalog/personalData/verifyCustomer.vue"
        ),
    },
    {
      path: "/personal-adress",
      name: "personalAdress",
      component: () =>
        import(
          /* webpackChunkName: "personalAdress" */ "@/catalog/personalAdress/personalAdress.vue"
        ),
    },
    {
      path: "/address",
      name: "address",
      component: () =>
        import(
          /* webpackChunkName: "address" */ "@/catalog/personalAdress/address.vue"
        ),
    },

    {
      path: "/privacy-policy",
      name: "privacyPolicy",
      component: () =>
        import(
          /* webpackChunkName: "privacyPolicy" */ "@/catalog/privacyPolicy/privacyPolicy.vue"
        ),
    },
    {
      path: "/payment/QrCode",
      name: "paymentQrCode",
      component: () =>
        import(
          /* webpackChunkName: "paymentQrCode" */ "@/catalog/summary/payment/paymentQrCode.vue"
        ),
    },
    {
      path: "/payment/select",
      name: "paymentSelect",
      component: () =>
        import(
          /* webpackChunkName: "paymentSelect" */ "@/catalog/summary/payment/paymentSelect.vue"
        ),
    },
    {
      path: "/payment/card",
      name: "paymentCard",
      component: () =>
        import(
          /* webpackChunkName: "paymentCard" */ "@/catalog/summary/payment/paymentCard.vue"
        ),
    },
    {
      path: "/payment/success",
      name: "paymentSuccess",
      component: () =>
        import(
          /* webpackChunkName: "paymentSuccess" */ "@/catalog/summary/payment/paymentSuccess.vue"
        ),
    },
    { path: "/payment", redirect: "/payment/select" },
    // Rotas dinâmicas do catálogo por último (evita capturar /summary, /cart, etc.)
    {
      path: "/:pdvId/:customerId/product/:productId",
      component: () => import("@/catalog/productDetail.vue"),
      name: "ProductDetail",
    },
    {
      path: "/:pdvId/product/:productId",
      component: () => import("@/catalog/productDetail.vue"),
      name: "ProductDetail-simple",
    },
    {
      path: "/:slug/:customerId",
      component: () => import("@/catalog/catalogPage.vue"),
      name: "catalog",
    },
    {
      path: "/:slug",
      component: () => import("@/catalog/catalogPage.vue"),
      name: "catalog-simple",
    },
    {
      path: "/404",
      name: "404",
      component: () =>
        import(/* webpackChunkName: "404" */ "@/components/404/page404.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () =>
        import(/* webpackChunkName: "404" */ "@/components/404/page404.vue"),
    },
  ],
});

/**
 * Guard global — não existia até aqui: o `meta.requiresAuth` espalhado pelas
 * rotas admin era puramente decorativo, e a única proteção real era o
 * interceptor de 401 do axios.
 *
 * A ORDEM dos early-returns importa e é o que mantém isto seguro:
 *  1. rota sem requiresAuth sai antes de tudo — o catálogo público (`/:slug`)
 *     nunca chega às checagens seguintes;
 *  2. sem sessão, vai para a home (login que funciona), não para `/user-auth`;
 *  3. só então o gate de onboarding, e nunca sobre a própria tela de onboarding
 *     (senão seria loop de redirect).
 */
router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some((record) => record.meta?.requiresAuth);
  if (!requiresAuth) return true;

  if (!sessionStorage.getItem("userToken")) {
    return { path: "/", query: { redirect: to.fullPath } };
  }

  if (!to.path.startsWith("/admin") || ONBOARDING_ALLOWLIST.includes(to.path)) {
    return true;
  }

  const pdvId = readCurrentPdvId();
  if (!pdvId) return true;

  // Nunca lança: o service engole erros e devolve null (fail-open), e o getter
  // trata null como "não bloquear".
  await store.dispatch("onboarding/ensureStatus", { pdvId });

  if (store.getters["onboarding/requiresAction"]) {
    return { path: ONBOARDING_PATH };
  }

  return true;
});

export default router;
